<template>
    <div class="setup-container">
        <div class="header-search">
            <div class="search-bar">
                <Search />
                <input 
                    type="text" 
                    placeholder="Search by member id, phone, email..." 
                    v-model="searchQuery" 
                    @input="handleSearch"
                />
            </div>
        </div>
        <div>
            <p class="application-title">Members</p>
        </div>
        <div v-if="searchQuery" class="search-info">
            <p>Showing {{ filteredData.length }} results for "{{ searchQuery }}"</p>
            <button @click="clearSearch" class="clear-search-btn">
                Clear search
            </button>
        </div>
        <!-- Table -->
        <table class="table">
            <thead>
                <tr>
                    <th>#</th>
                    <th>Member ID</th>
                    <th>Fullname</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="paginatedData.length === 0">
                    <td colspan="8" class="no-data">
                        {{ searchQuery ? 'No results found' : 'No data available' }}
                    </td>
                </tr>
                <tr v-else v-for="(item, index) in paginatedData" :key="item.id">
                    <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                    <td>{{ item.memberId }}</td>
                    <td>{{ item.lastname }} {{ item.firstname }} {{ item.othernames }}</td>
                    <td>{{ item.phone }}</td>
                    <td>{{ item.email }}</td>
                    <td>
                        <span class="text-warning" v-if="item.application_fee === 'pending'">{{ item.status }}</span>
                        <span class="text-success" v-else>{{ item.application_fee }}</span>
                    </td>
                    <td>
                        <span class="text-warning" v-if="item.status === 'pending'">{{ item.status }}</span>
                        <span class="text-success" v-else>{{ item.status }}</span>
                    </td>
                    <td class="actions">
                        <button @click="viewItem(item)" class="btn view">
                            <ArrowUpRight class="icon" />
                        </button>
                        <button @click="editItem(item)" class="btn edit">
                            <PenLine class="icon" />
                        </button>
                        <button @click="deleteItem(item)" class="btn delete">
                            <Trash class="icon" />
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1">
                <ChevronLeft />
            </button>
            <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
                :class="{ active: currentPage === page }">
                {{ page }}
            </button>
            <button @click="nextPage" :disabled="currentPage === totalPages">
                <ChevronRight />
            </button>
        </div>
    </div>
    <div
        v-if="showModal"
        class="modal fade show"
        style="display: block; background: rgba(0, 0, 0, 0.6);"
        @click.self="closeModal"
        >
        <div class="modal-dialog modal-dialog-centered modal-lg"  ref="printSection">
          <div class="modal-content rounded-3 shadow-lg border-0">
            <!-- Header -->
            <div class="modal-header bg-primary text-white rounded-top">
              <h5 class="modal-title fw-bold">
                <i class="fa fa-id-card me-2"></i> Members Preview
              </h5>
              <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-4" ref="printSection">
              <div v-if="selectedItem">
                <!-- Applicant Information -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Member Information
                </h6>
                <img style="border-radius: 4px;" :src="base_url + selectedItem.passport" width="150px" height="150px" alt="">
                <div class="row g-3 mb-4 mt-2">
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">First Name</label>
                    <p class="text-dark mb-0">{{ selectedItem.firstname }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Last Name</label>
                    <p class="text-dark mb-0">{{ selectedItem.lastname }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Other Name</label>
                    <p class="text-dark mb-0">{{ selectedItem.othernames }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Email</label>
                    <p class="text-dark mb-0">{{ selectedItem.email }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Phone</label>
                    <p class="text-dark mb-0">{{ selectedItem.phone }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Address</label>
                    <p class="text-dark mb-0">{{ selectedItem.address }}</p>
                  </div>
                </div>

                <!-- Application Details -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Application Details
                </h6>
                <div class="row g-3 mb-4">
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Application ID</label>
                    <p class="text-dark mb-0">{{ selectedItem.application_id }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Member ID</label>
                    <p class="text-dark mb-0">{{ selectedItem.memberId }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Profession</label>
                    <p class="text-dark mb-0">{{ selectedItem.profession }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Employer</label>
                    <p class="text-dark mb-0">{{ selectedItem.employer }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Employer Address</label>
                    <p class="text-dark mb-0">{{ selectedItem.empl_office_address }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Employer Phone</label>
                    <p class="text-dark mb-0">{{ selectedItem.empl_office_phone }}</p>
                  </div>

                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Nature of Business</label>
                    <p class="text-dark mb-0">{{ selectedItem.work_status }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Present Membership</label>
                    <p class="text-dark mb-0">{{ selectedItem.other_club_membership }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Expelled?</label>
                    <p class="text-dark mb-0">{{ selectedItem.expelled }}</p>
                  </div>

                  <div class="col-md-4">
                    <label class="fw-semibold text-muted">Expelled Reason?</label>
                    <p class="text-dark mb-0">{{ selectedItem.reason_expelled }}</p>
                  </div>

                  <!-- Game Interest -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                Game Interest
                </h6>
                <div class="row g-0 mb-0">
                <div class="col-md-12">
                    <ul class="list-inline mb-0">
                    <li
                        v-for="(game, index) in parsedGameInterest"
                        :key="index"
                        class="badge bg-info text-dark me-2 mb-0 px-3 py-2 rounded-pill"
                    >
                        {{ game }}
                    </li>
                    </ul>
                </div>
                </div>

                  

                </div>

                <!-- Children -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Children
                </h6>
                <div class="table-responsive mb-4">
                  <table class="table table-sm table-striped align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Sex</th>
                        <th>Date of Birth</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(child, index) in parsedChildren" :key="index">
                        <td>{{ child.name }}</td>
                        <td>{{ child.sex }}</td>
                        <td>{{ child.dob }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Sponsors -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Sponsor Information
                </h6>
                <div class="row g-3 mb-4">
                  <div class="col-md-6">
                    <label class="fw-semibold text-muted">First Sponsor</label>
                    <p class="text-dark mb-0">
                      {{ selectedItem.first_sponsor_firstname }} {{ selectedItem.first_sponsor_lastname }}
                      <br />
                      <small class="text-muted">Member ID: {{ selectedItem.first_sponsorId }}</small>
                      <br />
                      <small class="text-muted">Approval Status: <span class="text-warning">{{ selectedItem.sponsor_approval }}</span></small>
                    </p>
                  </div>
                  <div class="col-md-6">
                    <label class="fw-semibold text-muted">Second Sponsor</label>
                    <p class="text-dark mb-0">
                      {{ selectedItem.second_sponsor_firstname }} {{ selectedItem.second_sponsor_lastname }}
                      <br />
                      <small class="text-muted">Member ID: {{ selectedItem.second_sponsorId }}</small>
                      <br />
                      <small class="text-muted">Approval Status: <span class="text-warning">{{ selectedItem.sponsor_approval2 }}</span></small>
                    </p>
                  </div>
                </div>

                <!-- Executives -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Executive Information
                </h6>
                <div class="row g-3 mb-4">
                  <div class="col-md-6">
                    <label class="fw-semibold text-muted">First Executive</label>
                    <p class="text-dark mb-0">
                      {{ selectedItem.first_executive_firstname }} {{ selectedItem.first_executive_lastname }}
                      <br />
                      <small class="text-muted">Member ID: {{ selectedItem.first_executiveId }}</small>
                      <br />
                      <small class="text-muted">Approval Status: <span class="text-warning">{{ selectedItem.executive_member_approval }}</span></small>
                    </p>
                  </div>
                  <div class="col-md-6">
                    <label class="fw-semibold text-muted">Second Executive</label>
                    <p class="text-dark mb-0">
                      {{ selectedItem.second_executive_firstname }} {{ selectedItem.second_executive_lastname }}
                      <br />
                      <small class="text-muted">Member ID: {{ selectedItem.second_executiveId }}</small>
                      <br />
                      <small class="text-muted">Approval Status: <span class="text-warning">{{ selectedItem.executive_approval2 }}</span></small>
                    </p>
                  </div>
                </div>

                <!-- Documents -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Document Information
                </h6>
                <div class="row g-3 mb-4">
                  <div class="col-md-4" v-if="selectedItem.bank_statement">
                    <label class="fw-semibold text-muted">6 months personal or company statement</label>
                    <a :href="base_url + selectedItem.bank_statement" target="_blank" class="text-primary d-block text-decoration-none">
                      View File
                    </a>
                  </div>
                  <div class="col-md-4" v-if="selectedItem.means_identification">
                    <label class="fw-semibold text-muted">Valid Means of Identification</label>
                    <a :href="base_url + selectedItem.means_identification" target="_blank" class="text-primary d-block text-decoration-none">
                      View File
                    </a>
                  </div>
                  <div class="col-md-4" v-if="selectedItem.recent_utility">
                    <label class="fw-semibold text-muted">Recent Utility Bill</label>
                    <a :href="base_url + selectedItem.recent_utility" target="_blank" class="text-primary d-block text-decoration-none">
                      View File
                    </a>
                  </div>
                </div>

                <!-- Signature -->
                <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
                  Signature
                </h6>
                <div class="text-center">
                  <img
                    v-if="selectedItem.signature"
                    :src="base_url + selectedItem.signature"
                    alt="Signature"
                    class="img-fluid border rounded shadow-sm"
                    style="max-width: 300px;"
                  />
                  <p class="mt-2 text-muted">
                    <strong>Name:</strong> {{ selectedItem.signature_name }}
                  </p>
                  <p class="text-muted">
                    <strong>Date Submitted:</strong> {{ selectedItem.submit_date }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer bg-light d-flex justify-content-end gap-2">
              <button class="" @click="printItem">
                <i class="fa fa-print me-1"></i> Print
              </button>

              <button class="btn btn-secondary" @click="closeModal">
                <i class="fa fa-times me-1"></i> Close
              </button>
            
            </div>
          </div>
        </div>
    </div>

    <div
    v-if="showEditModal"
    class="modal fade show"
    style="display: block; background: rgba(0, 0, 0, 0.6);"
    @click.self="closeEditModal"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content rounded-3 shadow-lg border-0">
        <!-- Header -->
        <div class="modal-header bg-primary text-white rounded-top">
          <h5 class="modal-title fw-bold">
            <i class="fa fa-id-card me-2"></i> Edit Details
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeEditModal"></button>
        </div>

        <!-- Body -->
        <div class="modal-body p-4">
          <div v-if="selectedItem">
            <!-- Applicant Information -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Members Information
            </h6>
            <div class="row g-3 mb-4">
              <div class="col-md-4">
                <label class="fw-semibold text-muted">First Name</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.firstname"
                  placeholder="First Name"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Last Name</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.lastname"
                  placeholder="Last Name"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Other Name</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.othernames"
                  placeholder="Other Name"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Email</label>
                <input 
                  type="email" 
                  class="form-control" 
                  v-model="selectedItem.email"
                  placeholder="Email"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Phone</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  v-model="selectedItem.phone"
                  placeholder="Phone"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Address</label>
                <textarea 
                  class="form-control" 
                  v-model="selectedItem.address"
                  placeholder="Address"
                  rows="2"
                ></textarea>
              </div>
            </div>

            <!-- Application Details -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Application Details
            </h6>
            <div class="row g-3 mb-4">
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Application Id</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.application_id"
                  placeholder="Application ID"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Member Id</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.memberId"
                  placeholder="Member ID"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Profession</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.profession"
                  placeholder="Profession"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Employer</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.employer"
                  placeholder="Employer"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Employer Address</label>
                <textarea 
                  class="form-control" 
                  v-model="selectedItem.empl_office_address"
                  placeholder="Employer Address"
                  rows="2"
                ></textarea>
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Employer Phone</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  v-model="selectedItem.empl_office_phone"
                  placeholder="Employer Phone"
                >
              </div>

              <div class="col-md-4">
                <label class="fw-semibold text-muted">Nature of Business</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.work_status"
                  placeholder="Nature of Business"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Present Membership</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="selectedItem.other_club_membership"
                  placeholder="Present Membership"
                >
              </div>
              <div class="col-md-4">
                <label class="fw-semibold text-muted">Expelled?</label>
                <select class="form-control" v-model="selectedItem.expelled">
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div class="col-md-4" v-if="selectedItem.expelled === 'Yes'">
                <label class="fw-semibold text-muted">Expelled Reason?</label>
                <textarea 
                  class="form-control" 
                  v-model="selectedItem.reason_expelled"
                  placeholder="Reason for expulsion"
                  rows="2"
                ></textarea>
              </div>
            </div>

            <!-- Game Interest -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Game Interest
            </h6>
            <div class="row g-0 mb-4">
              <div class="col-md-12">
                <div class="d-flex flex-wrap gap-2">
                  <div 
                    v-for="(game, index) in availableGames" 
                    :key="index"
                    class="form-check"
                  >
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :value="game" 
                      :id="'game-' + index"
                      v-model="selectedItem.gameInterest"
                    >
                    <label class="form-check-label" :for="'game-' + index">
                      {{ game }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Children -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Children
            </h6>
            <div class="table-responsive mb-4">
              <table class="table table-sm table-striped align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Sex</th>
                    <th>Date of Birth</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(child, index) in selectedItem.children" :key="index">
                    <td>
                      <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="child.name"
                        placeholder="Child Name"
                      >
                    </td>
                    <td>
                      <select class="form-control form-control-sm" v-model="child.sex">
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </td>
                    <td>
                      <input 
                        type="date" 
                        class="form-control form-control-sm" 
                        v-model="child.dob"
                      >
                    </td>
                    <td>
                      <!-- <button 
                        class="btn btn-sm btn-outline-danger" 
                        @click="removeChild(index)"
                      >
                        <i class="fa fa-trash"></i>
                      </button> -->
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- <button class="btn btn-sm btn-outline-primary" @click="addChild">
                <i class="fa fa-plus me-1"></i> Add Child
              </button> -->
            </div>

            <!-- Sponsors -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Sponsor Information
            </h6>
            <div class="row g-3 mb-4">
              <div class="col-md-6">
                <label class="fw-semibold text-muted">First Sponsor</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_sponsor_firstname"
                      placeholder="First Name"
                    >
                  </div>
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_sponsor_lastname"
                      placeholder="Last Name"
                    >
                  </div>
                  <div class="col-12">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_sponsorId"
                      placeholder="Sponsor ID"
                    >
                  </div>
                
                </div>
              </div>
              <div class="col-md-6">
                <label class="fw-semibold text-muted">Second Sponsor</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_sponsor_firstname"
                      placeholder="First Name"
                    >
                  </div>
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_sponsor_lastname"
                      placeholder="Last Name"
                    >
                  </div>
                  <div class="col-12">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_sponsorId"
                      placeholder="Sponsor ID"
                    >
                  </div>
                 
                </div>
              </div>
            </div>

            <!-- Executives -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Executive Information
            </h6>
            <div class="row g-3 mb-4">
              <div class="col-md-6">
                <label class="fw-semibold text-muted">First Executive</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_executive_firstname"
                      placeholder="First Name"
                    >
                  </div>
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_executive_lastname"
                      placeholder="Last Name"
                    >
                  </div>
                  <div class="col-12">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.first_executiveId"
                      placeholder="Executive ID"
                    >
                  </div>
                
                </div>
              </div>
              <div class="col-md-6">
                <label class="fw-semibold text-muted">Second Executive</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_executive_firstname"
                      placeholder="First Name"
                    >
                  </div>
                  <div class="col-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_executive_lastname"
                      placeholder="Last Name"
                    >
                  </div>
                  <div class="col-12">
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="selectedItem.second_executiveId"
                      placeholder="Executive ID"
                    >
                  </div>
                 
                </div>
              </div>
            </div>

            <!-- Documents -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Document Information
            </h6>
            <div class="row g-3 mb-4">
              <div class="col-md-4" v-if="selectedItem.bank_statement">
                <label class="fw-semibold text-muted">6 months personal or company statement</label>
                <a :href="base_url + selectedItem.bank_statement" target="_blank" class="text-primary d-block text-decoration-none">
                  View File
                </a>
                <!-- <input 
                  type="file" 
                  class="form-control mt-2" 
                  @change="handleFileUpload($event, 'bank_statement')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                > -->
              </div>
              <div class="col-md-4" v-if="selectedItem.means_identification">
                <label class="fw-semibold text-muted">Valid Means of Identification</label>
                <a :href="base_url + selectedItem.means_identification" target="_blank" class="text-primary d-block text-decoration-none">
                  View File
                </a>
                <!-- <input 
                  type="file" 
                  class="form-control mt-2" 
                  @change="handleFileUpload($event, 'means_identification')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                > -->
              </div>
              <div class="col-md-4" v-if="selectedItem.recent_utility">
                <label class="fw-semibold text-muted">Recent Utility Bill</label>
                <a :href="base_url + selectedItem.recent_utility" target="_blank" class="text-primary d-block text-decoration-none">
                  View File
                </a>
                <!-- <input 
                  type="file" 
                  class="form-control mt-2" 
                  @change="handleFileUpload($event, 'recent_utility')"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                > -->
              </div>
            </div>

            <!-- Signature -->
            <h6 class="fw-bold mb-3 border-bottom pb-2 text-secondary">
              Signature
            </h6>
            <div class="text-center">
              <img
                v-if="selectedItem.signature"
                :src="base_url + selectedItem.signature"
                alt="Signature"
                class="img-fluid border rounded shadow-sm mb-3"
                style="max-width: 300px;"
              />
              <!-- <input 
                type="file" 
                class="form-control w-50 mx-auto" 
                @change="handleFileUpload($event, 'signature')"
                accept=".jpg,.jpeg,.png,.gif"
              > -->
              <p class="mt-2 text-muted">
                <strong></strong> 
                <input 
                  type="hidden" 
                  class="form-control d-inline-block w-auto mx-2" 
                  v-model="selectedItem.signature_name"
                  placeholder="Signature Name"
                >
              </p>
              <p class="text-muted">
                <strong>Date Submitted:</strong> {{ selectedItem.submit_date }}
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer bg-light d-flex justify-content-end gap-2">
          <button class="btn btn-success" @click="updateData">
            <i class="fa fa-save me-1"></i> Update
          </button>
          <button class="btn btn-secondary" @click="closeEditModal">
            <i class="fa fa-times me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
</div>

</template>

<script setup>
/* eslint-disable */ 

import { ArrowUpRight, ChevronLeft, ChevronRight, PenLine, Search, Trash } from "lucide-vue-next";
import { ref, watch, computed, onMounted, onBeforeUnmount } from "vue";
import axios from 'axios';

const dataList = ref([]);
const showModal = ref(false);
const showEditModal = ref(false);
const selectedItem = ref(null);
const searchQuery = ref('');
let base_url = process.env.VUE_APP_BASE_LIVE_URL
//let base_url = "http://localhost:8888/lltc_backend/"

// Pagination state
const currentPage = ref(1);
const pageSize = 50;
const totalRecords = ref(0);

const printSection = ref(null);

const printItem = () => {
  const printContents = printSection.value.innerHTML;
  const originalContents = document.body.innerHTML;

  // Replace full body with modal content
  document.body.innerHTML = printContents;

  window.print();

  // Restore original content
  document.body.innerHTML = originalContents;
  window.location.reload(); // ensures Vue re-renders properly
};


onMounted( async () => {
  await listRecords();
  
});

onBeforeUnmount( async () => {
  
});

// Computed values
const totalPages = computed(() =>
    Math.ceil(dataList.value.length / pageSize)
);

const parsedGameInterest = computed(() => {
  try {
    if (selectedItem.value && selectedItem.value.game_interest) {
      const interest = selectedItem.value.game_interest;
      return Array.isArray(interest)
        ? interest
        : JSON.parse(interest);
    }
    return [];
  } catch (err) {
    return [];
  }
});

const parsedChildren = computed(() => {
    try {
    if (selectedItem.value && selectedItem.value.children) {
      const child = selectedItem.value.children;
      return Array.isArray(child)
        ? child
        : JSON.parse(child);
    }
    return [];
  } catch (err) {
    return [];
  }
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredData.value.slice(start, start + pageSize);
});

// Pagination methods
function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    listRecords(page);
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    listRecords(currentPage.value - 1);
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    listRecords(currentPage.value + 1);
  }
}
watch(searchQuery, () => {
  currentPage.value = 1;
});
const filteredData = computed(() => {
  if (!searchQuery.value) {
    return dataList.value;
  }
  
  const query = searchQuery.value.toLowerCase().trim();
  
  return dataList.value.filter(item => {
    return (
      (item.firstname && item.firstname.toLowerCase().includes(query)) ||
      (item.lastname && item.lastname.toLowerCase().includes(query)) ||
      (item.memberId && item.memberId.toLowerCase().includes(query)) ||
      (item.email && item.email.toLowerCase().includes(query)) ||
      (item.phone && item.phone.toLowerCase().includes(query))
    );
  });
});

// Action methods
const listRecords = async () => {
  try {
    const response = await axios.get('/members_lists.php', {
    });

    if (response.data.status) {
      dataList.value = response.data.data;
    } else {
      console.error(response.data.message);
    }
  } catch (error) {
    console.error(error.message);
  }
};

function clearSearch() {
  searchQuery.value = '';
  currentPage.value = 1;
}
function viewItem(data) {
    console.log(data)
    selectedItem.value = data;
    showModal.value = true;
}
function editItem(data) {
    console.log(data)
    selectedItem.value = data;
    showEditModal.value = true;
}
function closeEditModal() {
  showEditModal.value = false;
}
function closeModal() {
  showModal.value = false;
}
function approveItem(item) {
  // You can trigger an API call here to approve the record
  showModal.value = false;
}

const updateData = async()=>{
    // Your logic to save the updated data
    console.log('Updating data:', selectedItem.value);
    try {
    const response = await axios.post("/update_members.php", {
      form_data: selectedItem.value,
      headers: { "Content-Type": "multipart/form-data" }
    });

    alert("✅ " + response.data.message);
    
     // Close modal after saving
     closeEditModal();

  } catch (error) {
    console.error(error);
    alert(error.response ? error.response.data.message : error.message);
  }
   
}
const deleteItem = async (item) => {
  if (confirm(`Are you sure you want to delete ${item.firstname}?`)) {
    try {
      const response = await axios.get(`/delete_application.php?id=${item.application_id}`);
      if (response.data.status) {
        alert(response.data.message);
        // Remove from local data
        dataList.value = dataList.value.filter(i => i.application_id !== item.application_id);
      } else {
        console.error(response.data.message);
      }
    } catch (error) {
      console.error('Delete failed:', error.message);
    }
  }
};
</script>

<style scoped>
.modal-content {
  border-radius: 12px;
  overflow-y: auto;
  max-height: 750px;
}
.btn-secondary{
    width: 80px !important;
}
.btn-success{
    width: 100px !important;
}
.modal-header {
  padding: 1rem 1.5rem;
}

.modal-body label {
  font-size: 0.85rem;
}

.modal-body p {
  font-size: 0.95rem;
}

.badge {
  font-size: 0.85rem;
  padding: 0.5em 0.8em;
  border-radius: 6px;
}

.setup-container {
    min-height: 100vh;
    padding: 24px;

}
.application-title{
    text-align: left;
}
/* Header: Setup button + search bar */
.header-search {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin-bottom: 16px;
}

/* Search bar */
.search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    padding: 8px 12px;
    flex: 1 1 200px;
    /* allows shrinking */
    max-width: 350px;
}

.search-bar input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 16px;
}

/* Table styles */
.table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow-x: auto;
}

.table thead {
    display: table-header-group;
}

.table tbody {
    display: table-row-group;
}

.table th,
.table td {
    border: 1px solid #E9ECEF;
    border-radius: 8px;
    box-shadow: #6C757D33 0px 1px 3px 0px, #6C757D1A 0px 1px 2px 0px;
    padding: 12px 16px;
    text-align: left;
    font-size: 14px;
}

.table th {
    background: #fff;
    color: #6C757D;
}

.actions {
    display: flex;
    gap: 6px;
}

.setup-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #fff;
    color: #DB6813;
    border: 1px solid #DB6813;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
}

/* Buttons */
.btn {
    width: 36px;
    height: 36px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    color: #343A40;
    background: white;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
}

.btn:hover {
    border: 1px solid #0DCAF0;
    color: #0DCAF0;
}

.btn .icon {
    width: 18px;
    height: 18px;
}

/* Pagination */
.pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
}

.pagination button {
    width: 36px;
    height: 36px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    color: #343A40;
    background: white;
    cursor: pointer;
}

.pagination button.active {
    border: 1px solid #0057FC;
    color: #0057FC;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #E9ECEF;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .setup-container {
        padding: 12px;
    }

    .table th,
    .table td {
        padding: 8px 10px;
        font-size: 12px;
    }

    .search-bar {
        flex: 1 1 100%;
        margin-top: 8px;
    }

    .setup-btn {
        padding: 6px 10px;
        font-size: 12px;
    }

    .btn {
        width: 32px;
        height: 32px;
    }

    .btn .icon {
        width: 16px;
        height: 16px;
    }

    .pagination button {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }

    /* Make table scrollable on mobile */
    .table {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }
}
</style>
